'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { THEMES, ThemeId } from '@/lib/storage';
import { getAllSettings, subscribeToSettings } from '@/lib/supabaseStorage';
import type { FullAdminSettings, HadistItem, DisplayInformasiItem } from '@/lib/supabaseStorage';
import { FALLBACK_TIMINGS, PRAYER_NAMES, addMinutes, timeToMinutes, formatDateID } from '@/lib/prayerTimes';

const PrayerScheduleTable = dynamic(() => import('./PrayerScheduleTable'), { ssr: false });
const IqomahCountdown = dynamic(() => import('./IqomahCountdown'), { ssr: false });
const BackgroundSlideshow = dynamic(() => import('./BackgroundSlideshow'), { ssr: false });
const RunningTextTicker = dynamic(() => import('./RunningTextTicker'), { ssr: false });
const ClockDisplay = dynamic(() => import('./ClockDisplay'), { ssr: false });

interface PrayerEntry {
  key: string;
  name: string;
  arabicName: string;
  adzan: string;
  iqomah: string;
  iqomahKey: string;
}

interface CountdownState {
  active: boolean;
  prayerName: string;
  arabicName: string;
  minutesLeft: number;
  secondsLeft: number;
  totalSecondsLeft: number;
}

// Phase: normal | last-minute | iqomah-blink | prayer-mode
type DisplayPhase = 'normal' | 'last-minute' | 'iqomah-blink' | 'prayer-mode';

export default function DisplayScreenClient() {
  const [settings, setSettings] = useState<FullAdminSettings | null>(null);
  const [timings, setTimings] = useState<Record<string, string>>(FALLBACK_TIMINGS);
  const [hijriDate, setHijriDate] = useState('');
  const [gregorianDate, setGregorianDate] = useState('');
  const [prayers, setPrayers] = useState<PrayerEntry[]>([]);
  const [activePrayerKey, setActivePrayerKey] = useState('');
  const [countdown, setCountdown] = useState<CountdownState>({
    active: false,
    prayerName: '',
    arabicName: '',
    minutesLeft: 0,
    secondsLeft: 0,
    totalSecondsLeft: 0,
  });
  const [apiLoaded, setApiLoaded] = useState(false);
  const [currentTime, setCurrentTime] = useState('');
  const [currentHadistIndex, setCurrentHadistIndex] = useState(0);
  const [hadistFade, setHadistFade] = useState(true);
  const [currentInfoIndex, setCurrentInfoIndex] = useState(0);
  const [infoFade, setInfoFade] = useState(true);

  // Phase management
  const [displayPhase, setDisplayPhase] = useState<DisplayPhase>('normal');
  const [blinkVisible, setBlinkVisible] = useState(true);
  const [blinkPrayerName, setBlinkPrayerName] = useState('');
  const [lastMinuteSeconds, setLastMinuteSeconds] = useState(59);

  // Refs to avoid stale closures in timers
  const phaseRef = useRef<DisplayPhase>('normal');
  const blinkTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prayerModeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setPhase = (p: DisplayPhase) => {
    phaseRef.current = p;
    setDisplayPhase(p);
  };

  // Load settings from Supabase and subscribe to real-time changes
  useEffect(() => {
    getAllSettings().then((s) => setSettings(s));
    const unsubscribe = subscribeToSettings((updatedSettings) => {
      setSettings(updatedSettings);
    });
    return () => { unsubscribe(); };
  }, []);

  // Fetch prayer times from equran.id using saved location from Supabase
  useEffect(() => {
    const provinsi = settings?.appSettings?.provinsi || 'Jawa Timur';
    const kabkota = settings?.appSettings?.kabkota || 'Kota Malang';

    const today = new Date();
    const bulan = today.getMonth() + 1;
    const tahun = today.getFullYear();
    const tanggal = today.getDate();

    fetch('https://equran.id/api/v2/shalat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ provinsi, kabkota, bulan, tahun }),
    })
      .then((res) => res.json())
      .then((data) => {
        const jadwal: Array<Record<string, string | number>> =
          Array.isArray(data?.data?.jadwal)
            ? data.data.jadwal
            : Array.isArray(data?.jadwal)
            ? data.jadwal
            : null;

        if (data?.code === 200 && jadwal) {
          const todaySchedule = jadwal.find((j) => Number(j.tanggal) === tanggal);
          if (todaySchedule) {
            setTimings({
              subuh:   String(todaySchedule.subuh   || FALLBACK_TIMINGS.subuh),
              terbit:  String(todaySchedule.terbit  || FALLBACK_TIMINGS.terbit),
              dzuhur:  String(todaySchedule.dzuhur  || FALLBACK_TIMINGS.dzuhur),
              ashar:   String(todaySchedule.ashar   || FALLBACK_TIMINGS.ashar),
              maghrib: String(todaySchedule.maghrib || FALLBACK_TIMINGS.maghrib),
              isya:    String(todaySchedule.isya    || FALLBACK_TIMINGS.isya),
            });
            setApiLoaded(true);
          }
        }
      })
      .catch(() => { setApiLoaded(false); });
  }, [settings?.appSettings?.provinsi, settings?.appSettings?.kabkota]);

  // Compute Gregorian date once
  useEffect(() => {
    const now = new Date();
    setGregorianDate(formatDateID(now));

    try {
      const hijriFormatter = new Intl.DateTimeFormat('id-ID-u-ca-islamic', {
        day: 'numeric', month: 'long', year: 'numeric',
      });
      const parts = hijriFormatter.formatToParts(now);
      const day = parts.find((p) => p.type === 'day')?.value || '';
      const monthRaw = parts.find((p) => p.type === 'month')?.value || '';
      const year = parts.find((p) => p.type === 'year')?.value || '';
      const hijriMonthMap: Record<string, string> = {
        Muharram: 'Muharram', Safar: 'Safar',
        'Rabi\u02bb al-Awwal': "Rabi'ul Awwal", 'Rabi\u02bb al-Th\u0101n\u012b': "Rabi'ul Akhir",
        'Jum\u0101d\u00e1 al-\u016Cl\u00e1': 'Jumadil Awwal', 'Jum\u0101d\u00e1 al-\u0100khirah': 'Jumadil Akhir',
        Rajab: 'Rajab', "Sha\u02bbb\u0101n": "Sya'ban",
        'Rama\u1e0d\u0101n': 'Ramadhan', Shaww\u0101l: 'Syawwal',
        'Dh\u016b al-Qa\u02bbdah': "Dzulqa'dah", 'Dh\u016b al-\u1e24ijjah': 'Dzulhijjah',
      };
      const monthID = hijriMonthMap[monthRaw] || monthRaw;
      setHijriDate(`${day} ${monthID} ${year} H`);
    } catch { /* leave empty */ }
  }, []);

  // Build prayer entries whenever timings or settings change
  useEffect(() => {
    const iqomah = settings?.iqomah ?? {
      subuh: 10, dzuhur: 15, ashar: 10, maghrib: 8, isya: 10,
    };
    const entries: PrayerEntry[] = PRAYER_NAMES.map((p) => {
      const adzan = (timings[p.key] || '').split(' ')[0];
      const delay = iqomah[p.iqomahKey];
      const iqomahTime = addMinutes(adzan, delay);
      return { key: p.key, name: p.name, arabicName: p.arabicName, adzan, iqomah: iqomahTime, iqomahKey: p.iqomahKey };
    });
    setPrayers(entries);
  }, [timings, settings]);

  // Auto-rotate hadist every 8 seconds
  useEffect(() => {
    const activeHadist = settings?.hadistList?.filter((h) => h.active) || [];
    if (activeHadist.length <= 1) return;
    const interval = setInterval(() => {
      setHadistFade(false);
      setTimeout(() => {
        setCurrentHadistIndex((prev) => (prev + 1) % activeHadist.length);
        setHadistFade(true);
      }, 500);
    }, 8000);
    return () => clearInterval(interval);
  }, [settings?.hadistList]);

  // Auto-rotate display informasi every 6 seconds
  useEffect(() => {
    const activeInfo = settings?.displayInformasiList?.filter((i) => i.active) || [];
    if (activeInfo.length <= 1) return;
    const interval = setInterval(() => {
      setInfoFade(false);
      setTimeout(() => {
        setCurrentInfoIndex((prev) => (prev + 1) % activeInfo.length);
        setInfoFade(true);
      }, 500);
    }, 6000);
    return () => clearInterval(interval);
  }, [settings?.displayInformasiList]);

  // Trigger IQOMAH blink phase then prayer mode
  const triggerIqomahBlink = useCallback((prayerName: string) => {
    if (phaseRef.current === 'iqomah-blink' || phaseRef.current === 'prayer-mode') return;

    const blinkDuration = settings?.appSettings?.iqomahBlinkDuration ?? 10;
    const prayerModeDuration = settings?.appSettings?.prayerModeDuration ?? 10;

    setBlinkPrayerName(prayerName);
    setPhase('iqomah-blink');
    setBlinkVisible(true);

    // Clear any existing timers
    if (blinkTimerRef.current) clearInterval(blinkTimerRef.current);
    if (prayerModeTimerRef.current) clearTimeout(prayerModeTimerRef.current);

    // Blink every 500ms
    blinkTimerRef.current = setInterval(() => {
      setBlinkVisible((v) => !v);
    }, 500);

    // After blinkDuration seconds, switch to prayer mode
    prayerModeTimerRef.current = setTimeout(() => {
      if (blinkTimerRef.current) {
        clearInterval(blinkTimerRef.current);
        blinkTimerRef.current = null;
      }
      setPhase('prayer-mode');

      // After prayerModeDuration minutes, return to normal
      prayerModeTimerRef.current = setTimeout(() => {
        setPhase('normal');
      }, prayerModeDuration * 60 * 1000);
    }, blinkDuration * 1000);
  }, [settings?.appSettings?.iqomahBlinkDuration, settings?.appSettings?.prayerModeDuration]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (blinkTimerRef.current) clearInterval(blinkTimerRef.current);
      if (prayerModeTimerRef.current) clearTimeout(prayerModeTimerRef.current);
    };
  }, []);

  // Real-time clock + countdown logic
  const tick = useCallback(() => {
    const now = new Date();
    const h = String(now.getHours()).padStart(2, '0');
    const m = String(now.getMinutes()).padStart(2, '0');
    const s = String(now.getSeconds()).padStart(2, '0');
    setCurrentTime(`${h}:${m}:${s}`);

    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const nowSeconds = nowMinutes * 60 + now.getSeconds();

    let nextPrayer: PrayerEntry | null = null;
    let activePrayer: PrayerEntry | null = null;

    for (const p of prayers) {
      const adzanMin = timeToMinutes(p.adzan);
      const iqomahMin = timeToMinutes(p.iqomah);
      if (nowMinutes >= adzanMin && nowMinutes < iqomahMin) {
        activePrayer = p;
        break;
      }
    }

    for (const p of prayers) {
      const iqomahMin = timeToMinutes(p.iqomah);
      if (nowMinutes < iqomahMin) {
        nextPrayer = p;
        break;
      }
    }

    setActivePrayerKey(activePrayer?.key || nextPrayer?.key || '');

    // Check for last-minute countdown (< 60 seconds to iqomah)
    let lastMinuteTriggered = false;
    let normalCountdownTriggered = false;

    for (const p of prayers) {
      const iqomahMin = timeToMinutes(p.iqomah);
      const iqomahSeconds = iqomahMin * 60;
      const diffToIqomah = iqomahSeconds - nowSeconds;

      // Last minute: 0 < diff <= 60 seconds
      if (diffToIqomah > 0 && diffToIqomah <= 60) {
        lastMinuteTriggered = true;
        setLastMinuteSeconds(diffToIqomah);

        // Only enter last-minute phase if currently normal or last-minute
        if (phaseRef.current === 'normal' || phaseRef.current === 'last-minute') {
          setPhase('last-minute');
          setCountdown({
            active: false,
            prayerName: p.name,
            arabicName: p.arabicName,
            minutesLeft: 0,
            secondsLeft: diffToIqomah,
            totalSecondsLeft: diffToIqomah,
          });
        }
        break;
      }

      // Exactly at iqomah time (diff <= 0 and > -5s) → trigger blink
      if (diffToIqomah <= 0 && diffToIqomah > -5) {
        if (phaseRef.current === 'last-minute') {
          triggerIqomahBlink(p.name);
        }
        break;
      }
    }

    // Normal countdown (1-5 min before adzan)
    if (!lastMinuteTriggered) {
      for (const p of prayers) {
        const adzanMin = timeToMinutes(p.adzan);
        const adzanSeconds = adzanMin * 60;
        const diffSeconds = adzanSeconds - nowSeconds;

        if (diffSeconds > 0 && diffSeconds <= 5 * 60) {
          const mins = Math.floor(diffSeconds / 60);
          const secs = diffSeconds % 60;
          setCountdown({ active: true, prayerName: p.name, arabicName: p.arabicName, minutesLeft: mins, secondsLeft: secs, totalSecondsLeft: diffSeconds });
          normalCountdownTriggered = true;
          break;
        }
      }
    }

    if (!lastMinuteTriggered && !normalCountdownTriggered) {
      setCountdown((prev) => ({ ...prev, active: false }));
      // Return to normal if we're in last-minute phase but no iqomah is near
      if (phaseRef.current === 'last-minute') {
        setPhase('normal');
      }
    }
  }, [prayers, triggerIqomahBlink]);

  useEffect(() => {
    if (prayers.length === 0) return;
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [tick, prayers]);

  const theme = settings ? THEMES[settings.selectedTheme as ThemeId] : THEMES['dark-green'];
  const activeTexts = settings?.runningTexts.filter((t) => t.active) || [];
  const photos = settings?.photos || [];
  const bgOpacity = settings?.appSettings?.bgOpacity ?? 35;
  const activeHadist: HadistItem[] = settings?.hadistList?.filter((h) => h.active) || [];
  const currentHadist = activeHadist.length > 0 ? activeHadist[currentHadistIndex % activeHadist.length] : null;
  const activeInfo: DisplayInformasiItem[] = settings?.displayInformasiList?.filter((i) => i.active) || [];
  const currentInfo = activeInfo.length > 0 ? activeInfo[currentInfoIndex % activeInfo.length] : null;
  const cityLabel = settings?.appSettings?.kabkota || 'Kota Malang';

  // ─── Prayer Mode: show only background ───────────────────────────────────
  if (displayPhase === 'prayer-mode') {
    return (
      <div
        className="relative w-screen h-screen overflow-hidden"
        style={{ backgroundColor: theme.bg }}
      >
        <BackgroundSlideshow photos={photos} bgOpacity={bgOpacity} />
        <div className="absolute inset-0" style={{ backgroundColor: `${theme.bg}55` }} />
        {/* Subtle indicator */}
        <div
          className="absolute bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full text-xs font-medium"
          style={{ backgroundColor: `${theme.accent}15`, color: `${theme.accent}60`, border: `1px solid ${theme.accent}20` }}
        >
          🕌 Waktu Sholat
        </div>
      </div>
    );
  }

  // ─── IQOMAH Blink overlay ─────────────────────────────────────────────────
  if (displayPhase === 'iqomah-blink') {
    return (
      <div
        className="relative w-screen h-screen overflow-hidden flex flex-col items-center justify-center"
        style={{ backgroundColor: theme.bg }}
      >
        <BackgroundSlideshow photos={photos} bgOpacity={Math.min(bgOpacity, 20)} />
        <div className="absolute inset-0" style={{ backgroundColor: `${theme.bg}cc` }} />
        <div
          className="relative z-10 flex flex-col items-center justify-center text-center"
          style={{ opacity: blinkVisible ? 1 : 0, transition: 'opacity 0.1s' }}
        >
          <p
            className="font-black tracking-widest"
            style={{
              fontSize: 'clamp(5rem, 15vw, 12rem)',
              color: theme.accent,
              textShadow: `0 0 60px ${theme.accent}99, 0 0 120px ${theme.accent}44`,
              fontFamily: 'serif',
              lineHeight: 1,
            }}
          >
            IQOMAH
          </p>
          {blinkPrayerName && (
            <p
              className="mt-4 text-2xl font-bold tracking-widest uppercase"
              style={{ color: `${theme.accent}cc` }}
            >
              {blinkPrayerName}
            </p>
          )}
          <p
            className="mt-3 text-base font-medium tracking-widest"
            style={{ color: `${theme.accent}66` }}
          >
            Tegakkan shaf — Luruskan barisan
          </p>
        </div>
        {/* Top/bottom accent lines */}
        <div className="absolute top-0 left-0 right-0 h-1 z-20" style={{ background: `linear-gradient(90deg, transparent, ${theme.accent}, transparent)` }} />
        <div className="absolute bottom-0 left-0 right-0 h-1 z-20" style={{ background: `linear-gradient(90deg, transparent, ${theme.accent}, transparent)` }} />
      </div>
    );
  }

  // ─── Last-minute countdown (< 60s to iqomah) ─────────────────────────────
  if (displayPhase === 'last-minute') {
    return (
      <div
        className="relative w-screen h-screen overflow-hidden flex flex-col items-center justify-center"
        style={{ backgroundColor: theme.bg }}
      >
        <BackgroundSlideshow photos={photos} bgOpacity={Math.min(bgOpacity, 25)} />
        <div className="absolute inset-0" style={{ backgroundColor: `${theme.bg}cc` }} />
        <div className="absolute top-0 left-0 right-0 h-1 z-20" style={{ background: `linear-gradient(90deg, transparent, ${theme.accent}, transparent)` }} />

        <div className="relative z-10 flex flex-col items-center text-center px-8">
          <p
            className="text-sm font-bold tracking-[0.3em] uppercase mb-4"
            style={{ color: `${theme.accent}88` }}
          >
            ⏰ Segera Iqomah — {countdown.prayerName}
          </p>
          <p
            className="text-8xl font-light mb-2"
            style={{ color: theme.accent, fontFamily: 'serif', textShadow: `0 0 40px ${theme.accent}66` }}
          >
            {countdown.arabicName}
          </p>
          <p
            className="text-4xl font-bold mb-10 tracking-wide"
            style={{ color: theme.accent }}
          >
            {countdown.prayerName}
          </p>

          {/* Big seconds countdown */}
          <div className="relative w-64 h-64 mb-8">
            <svg className="absolute inset-0 -rotate-90" width="256" height="256" viewBox="0 0 240 240">
              <circle cx="120" cy="120" r="110" fill="none" stroke={`${theme.accent}15`} strokeWidth="10" />
              <circle
                cx="120" cy="120" r="110" fill="none"
                stroke={theme.accent} strokeWidth="10" strokeLinecap="round"
                strokeDasharray={2 * Math.PI * 110}
                strokeDashoffset={2 * Math.PI * 110 * (1 - lastMinuteSeconds / 60)}
                style={{ transition: 'stroke-dashoffset 1s linear', filter: `drop-shadow(0 0 8px ${theme.accent})` }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span
                className="text-8xl font-bold tabular-nums leading-none"
                style={{ color: theme.accent, textShadow: `0 0 30px ${theme.accent}88` }}
              >
                {String(lastMinuteSeconds).padStart(2, '0')}
              </span>
              <span className="text-sm mt-2 font-medium tracking-widest uppercase" style={{ color: `${theme.accent}66` }}>
                detik
              </span>
            </div>
          </div>

          <p className="text-xl font-semibold" style={{ color: `${theme.accent}cc` }}>
            Bersiaplah untuk sholat berjamaah
          </p>
          <p className="text-base mt-1" style={{ color: `${theme.accent}55` }}>
            Sempurnakan wudhu dan luruskan shaf
          </p>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-1 z-20" style={{ background: `linear-gradient(90deg, transparent, ${theme.accent}, transparent)` }} />
      </div>
    );
  }

  // ─── Normal display ───────────────────────────────────────────────────────
  return (
    <div
      className="relative w-screen h-screen overflow-hidden flex flex-col"
      style={{ backgroundColor: theme.bg, fontFamily: 'var(--font-plus-jakarta-sans)' }}
    >
      {/* Background slideshow */}
      <BackgroundSlideshow photos={photos} bgOpacity={bgOpacity} />

      {/* Dark overlay with Islamic pattern */}
      <div className="absolute inset-0 islamic-pattern" style={{ backgroundColor: `${theme.bg}cc` }} />

      {/* Decorative top border */}
      <div className="absolute top-0 left-0 right-0 h-1 z-20" style={{ background: `linear-gradient(90deg, transparent, ${theme.accent}, transparent)` }} />

      {/* Main content */}
      <div className="relative z-10 flex flex-col h-full">
        {/* Header: Mosque name */}
        <header className="flex items-center justify-between px-10 pt-6 pb-2">
          <div className="flex items-center gap-4">
            <Image
              src="/assets/images/logo-smk5-1779877074693.png"
              alt="Logo SMK Negeri 5 Malang"
              width={64}
              height={64}
              className="object-contain flex-shrink-0"
              style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.4))' }}
            />
            <div className="flex flex-col">
              <p className="text-base font-bold tracking-widest uppercase" style={{ color: '#ffffff' }}>
                SMK Negeri 5 Malang
              </p>
              <h1 className="text-3xl font-bold tracking-wide" style={{ color: theme.accent }}>
                Masjid Baitul Gufron
              </h1>
            </div>
          </div>
          <div className="flex flex-col items-end">
            <p className="text-sm font-medium" style={{ color: `${theme.accent}aa` }}>{gregorianDate}</p>
            <p className="text-base font-semibold mt-0.5" style={{ color: theme.accent }}>
              {hijriDate || '6 Dzulhijjah 1447 H'}
            </p>
          </div>
        </header>

        {/* Ornament line */}
        <div className="mx-10 my-1 ornament-divider" />

        {/* Main body */}
        <main className="flex flex-1 gap-6 px-10 py-3 overflow-hidden">
          {/* Left: Clock + Prayer Schedule */}
          <div className="flex flex-col w-96 flex-shrink-0 overflow-hidden">
            <div className="flex flex-col items-center justify-center">
              <ClockDisplay currentTime={currentTime} accentColor={theme.accent} />
              <div className="mt-2 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: apiLoaded ? '#22c55e' : '#f59e0b' }} />
                <span className="text-xs" style={{ color: `${theme.accent}66` }}>
                  {apiLoaded ? `Waktu Sholat Terverifikasi` : 'Waktu Sholat Estimasi'}
                </span>
              </div>
            </div>

            <div className="my-2 h-px" style={{ backgroundColor: `${theme.accent}20` }} />

            <div className="flex flex-col flex-1 overflow-hidden">
              <div className="mb-2">
                <h2 className="text-lg font-bold tracking-wide" style={{ color: theme.accent }}>Jadwal Sholat</h2>
                <p className="text-xs font-bold tracking-wide mt-0.5" style={{ color: theme.accent }}>
                  {cityLabel} — Metode Kemenag RI
                </p>
              </div>
              <div className="flex-1 overflow-hidden">
                <PrayerScheduleTable prayers={prayers} activePrayerKey={activePrayerKey} accentColor={theme.accent} bgColor={theme.card} />
              </div>
            </div>
          </div>

          {/* Vertical divider */}
          <div className="w-px self-stretch" style={{ backgroundColor: `${theme.accent}20` }} />

          {/* Right column */}
          <div className="flex-1 flex flex-col gap-3 overflow-hidden">
            {/* TOP: Hadist / Kata-kata Mutiara */}
            <div
              className="flex-1 flex flex-col items-center justify-start rounded-2xl px-6 py-3 overflow-hidden min-h-0"
              style={{
                background: `linear-gradient(135deg, ${theme.accent}22 0%, ${theme.accent}10 50%, rgba(0,0,0,0.3) 100%)`,
                border: `1px solid ${theme.accent}40`,
                boxShadow: `inset 0 1px 0 ${theme.accent}30, 0 4px 20px rgba(0,0,0,0.3)`,
              }}
            >
              <div
                className="flex items-center gap-2 mb-3 px-3 py-1 rounded-full"
                style={{ backgroundColor: `${theme.accent}20`, border: `1px solid ${theme.accent}30` }}
              >
                <span className="text-xs font-bold tracking-widest uppercase" style={{ color: theme.accent }}>
                  ✦ Hadist &amp; Mutiara ✦
                </span>
              </div>

              {currentHadist ? (
                <div
                  className="flex flex-col items-center text-center gap-2 w-full"
                  style={{ opacity: hadistFade ? 1 : 0, transition: 'opacity 0.5s ease-in-out' }}
                >
                  <p
                    className="font-semibold leading-relaxed line-clamp-3"
                    style={{ color: theme.accent, fontFamily: 'serif', textShadow: '0 1px 3px rgba(0,0,0,0.4)', fontSize: `${settings?.appSettings?.hadistFontSize ?? 48}px` }}
                  >
                    {currentHadist.text}
                  </p>
                  {currentHadist.source && (
                    <p className="text-base font-medium" style={{ color: `${theme.accent}88` }}>
                      — {currentHadist.source}
                    </p>
                  )}
                  {activeHadist.length > 1 && (
                    <div className="flex gap-1.5 mt-1">
                      {activeHadist.map((_, i) => (
                        <span
                          key={`hdot-${i}`}
                          className="w-1.5 h-1.5 rounded-full transition-all duration-300"
                          style={{
                            backgroundColor: i === (currentHadistIndex % activeHadist.length)
                              ? theme.accent
                              : `${theme.accent}30`,
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center">
                  <p className="font-light mb-1" style={{ color: `${theme.accent}55`, fontFamily: 'serif', fontSize: `${settings?.appSettings?.mutiaraFontSize ?? 48}px` }}>
                    الصلاة خير من النوم
                  </p>
                  <p className="text-base" style={{ color: `${theme.accent}44` }}>Sholat lebih baik dari tidur</p>
                </div>
              )}
            </div>

            {/* BOTTOM: Display Informasi slideshow */}
            <div
              className="flex-1 flex flex-col overflow-hidden rounded-2xl"
              style={{ border: `1px solid ${theme.accent}20`, backgroundColor: `${theme.card}40` }}
            >
              {currentInfo ? (
                <div
                  className="flex flex-col h-full"
                  style={{ opacity: infoFade ? 1 : 0, transition: 'opacity 0.5s ease-in-out' }}
                >
                  <div
                    className="flex-shrink-0 flex items-center justify-between px-4 py-2"
                    style={{ backgroundColor: `${theme.accent}18`, borderBottom: `1px solid ${theme.accent}20` }}
                  >
                    <p className="text-sm font-bold tracking-wide truncate" style={{ color: theme.accent }}>
                      {currentInfo.judul}
                    </p>
                    {activeInfo.length > 1 && (
                      <div className="flex gap-1 flex-shrink-0 ml-3">
                        {activeInfo.map((_, i) => (
                          <span
                            key={`idot-${i}`}
                            className="w-1.5 h-1.5 rounded-full transition-all duration-300"
                            style={{
                              backgroundColor: i === (currentInfoIndex % activeInfo.length)
                                ? theme.accent
                                : `${theme.accent}30`,
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 relative overflow-hidden">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={currentInfo.imageUrl}
                      alt={currentInfo.judul}
                      className="w-full h-full object-contain"
                      style={{ backgroundColor: 'rgba(0,0,0,0.1)' }}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${theme.accent}15`, border: `1px solid ${theme.accent}20` }}
                  >
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={`${theme.accent}60`} strokeWidth="1.5">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <polyline points="21 15 16 10 5 21" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium" style={{ color: `${theme.accent}55` }}>Display Informasi</p>
                  <p className="text-xs mt-1" style={{ color: `${theme.accent}33` }}>Upload gambar dari panel admin</p>
                </div>
              )}
            </div>
          </div>
        </main>

        {/* Running text ticker */}
        <div className="relative z-20 mt-auto" style={{ borderTop: `1px solid ${theme.accent}30` }}>
          <div className="flex items-center" style={{ backgroundColor: `${theme.accent}18` }}>
            <div
              className="flex-shrink-0 px-4 py-2.5 text-xs font-bold tracking-widest uppercase"
              style={{ backgroundColor: theme.accent, color: theme.bg, minWidth: '120px', textAlign: 'center' }}
            >
              INFO MASJID
            </div>
            <RunningTextTicker texts={activeTexts} accentColor={theme.accent} />
          </div>
        </div>
      </div>

      {/* Iqomah countdown overlay (5-min before adzan) */}
      {countdown.active && (
        <IqomahCountdown countdown={countdown} accentColor={theme.accent} bgColor={theme.bg} cardColor={theme.card} />
      )}
    </div>
  );
}